import { createListenerMiddleware } from '@reduxjs/toolkit'
import { nanoid } from 'nanoid'
import { AppDispatch, RootState } from '@/store/store'
import { UserFoldersActions } from './slice'
import { UserFilesActions } from '@/user_files/files/store/slice'
import { ApiError } from '@/util/errors/api-error'
import { MattersActions } from '@/matter/store/slice'
import { MatterRead, MatterType } from '@/matter/store/schemas'
import { isActiveMatterMember } from '@/matter/utils/matter-permissions'
import { type DriveListSort, driveListSortsEqual, kDefaultDriveListSort } from '@/user_files/sorting'

// Import fetch functions
import { getRootFolder } from '../fetch/get-root-folder'
import { getFolder } from '../fetch/get-folder'
import { getFolderChildren } from '../fetch/get-folder-children'
import { restoreFolder as restoreFolderApi } from '../fetch/post-restore-folder'
import { isAbortError } from '@/util/errors/is-abort-error'

export const userFoldersListenerMiddleware = createListenerMiddleware()
const startListener = userFoldersListenerMiddleware.startListening.withTypes<RootState, AppDispatch>()
/**
 * One in-flight children load per folder, with the sort it was issued under.
 *
 * Requests always refresh, even when children are cached. A second request for the same folder
 * either queues one follow-up refresh (same sort) or supersedes the load (different sort — the
 * older request is aborted). Without the abort, a slow load could overwrite the new sort's rows.
 */
const inFlightFolderChildrenLoads = new Map<string, { sort: DriveListSort; controller: AbortController }>()
// Queue folder ids only; the latest non-superseded load supplies the sort when draining the queue.
const queuedFolderChildrenRefreshes = new Set<string>()

function getApiStatusCode(error: unknown): number | undefined {
  return error instanceof ApiError ? error.status : undefined
}

function isMissingOrDeniedStatus(statusCode: number | undefined): boolean {
  return statusCode === 403 || statusCode === 404
}

function getMatterFolderIds(state: RootState['userFoldersState'], matterId: string): string[] {
  return Object.values(state.folders)
    .filter((folderState) => folderState.data.matter_id === matterId)
    .map((folderState) => folderState.data.id)
}

function isMatterFilesMemberVisible(matter: MatterRead | undefined): boolean {
  if (!matter) return false
  if (matter.type !== MatterType.STANDARD && matter.type !== MatterType.SAMPLE) return false
  return isActiveMatterMember(matter)
}

// ============================================================================
// Fetch Root Folder
// ============================================================================

startListener({
  actionCreator: UserFoldersActions.fetchRootFolder,
  effect: async (action, listenerApi) => {
    const { matterId, force = false } = action.payload
    const requestId = nanoid()

    // Check if already loaded
    const state = listenerApi.getState().userFoldersState
    if (!force && (state.rootByMatterId[matterId] || state.rootFolderRequestsByMatterId[matterId]?.status === 'loading')) {
      return // Already have root folder
    }

    try {
      listenerApi.dispatch(UserFoldersActions.fetchRootFolderStarted({ matterId }))
      listenerApi.dispatch(
        UserFoldersActions.requestStarted({
          requestId,
          operationType: 'fetchRoot',
        })
      )

      const response = await getRootFolder(matterId)

      listenerApi.dispatch(UserFoldersActions.upsertFolder(response.folder))
      listenerApi.dispatch(UserFoldersActions.fetchRootFolderSucceeded({ matterId }))
      listenerApi.dispatch(UserFoldersActions.requestSucceeded({ requestId }))
      listenerApi.dispatch(UserFoldersActions.setInitialLoadComplete(true))
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to fetch root folder'
      const statusCode = getApiStatusCode(error)

      if (isMissingOrDeniedStatus(statusCode)) {
        const folderIds = getMatterFolderIds(listenerApi.getState().userFoldersState, matterId)
        listenerApi.dispatch(UserFilesActions.clearMatterFiles({ matterId, folderIds }))
        listenerApi.dispatch(UserFoldersActions.clearMatterFolders({ matterId }))
      }

      listenerApi.dispatch(UserFoldersActions.fetchRootFolderFailed({ matterId, error: errorMessage, statusCode }))
      listenerApi.dispatch(UserFoldersActions.requestFailed({ requestId, error: errorMessage }))
    }
  },
})

// ============================================================================
// Clear Matter Files state when matter-list access disappears
// ============================================================================

startListener({
  actionCreator: MattersActions.setMatters,
  effect: (action, listenerApi) => {
    const originalState = listenerApi.getOriginalState()
    const nextMatters = new Map(action.payload.map((matter) => [matter.id, matter]))

    Object.entries(originalState.mattersState.matters).forEach(([matterId, previousMatter]) => {
      if (!isMatterFilesMemberVisible(previousMatter)) return
      if (isMatterFilesMemberVisible(nextMatters.get(matterId))) return

      const folderIds = getMatterFolderIds(originalState.userFoldersState, matterId)
      listenerApi.dispatch(UserFilesActions.clearMatterFiles({ matterId, folderIds }))
      listenerApi.dispatch(UserFoldersActions.clearMatterFolders({ matterId }))
    })
  },
})

startListener({
  actionCreator: MattersActions.removeMatter,
  effect: (action, listenerApi) => {
    const originalState = listenerApi.getOriginalState()
    const { matterId } = action.payload
    const folderIds = getMatterFolderIds(originalState.userFoldersState, matterId)

    listenerApi.dispatch(UserFilesActions.clearMatterFiles({ matterId, folderIds }))
    listenerApi.dispatch(UserFoldersActions.clearMatterFolders({ matterId }))
  },
})

// ============================================================================
// Fetch Folder by ID
// ============================================================================

startListener({
  actionCreator: UserFoldersActions.fetchFolderById,
  effect: async (action, listenerApi) => {
    const { folderId } = action.payload
    const requestId = nanoid()

    try {
      listenerApi.dispatch(
        UserFoldersActions.requestStarted({
          requestId,
          operationType: 'fetchFolder',
          folderId,
        })
      )

      const folder = await getFolder(folderId)

      listenerApi.dispatch(UserFoldersActions.upsertFolder(folder))
      listenerApi.dispatch(UserFoldersActions.requestSucceeded({ requestId }))
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to fetch folder'
      listenerApi.dispatch(UserFoldersActions.requestFailed({ requestId, error: errorMessage }))
    }
  },
})

// ============================================================================
// Fetch Folder Children
// ============================================================================

startListener({
  actionCreator: UserFoldersActions.fetchFolderChildren,
  effect: async (action, listenerApi) => {
    const { folderId } = action.payload
    const requestId = nanoid()
    const folderState = listenerApi.getState().userFoldersState.folders[folderId]
    const inFlight = inFlightFolderChildrenLoads.get(folderId)
    // A caller with no sorting UI (the file picker, the move dialog) means "reload this folder", not
    // "reorder it", so it inherits an order rather than choosing one. An in-flight load is a more
    // current answer than the cache: while a sort change is being fetched the cache still describes
    // the sort being replaced, and inheriting *that* would make this refresh look like a different
    // request and supersede the sort the user just asked for.
    const sort = action.payload.sort ?? inFlight?.sort ?? folderState?.childrenSort ?? kDefaultDriveListSort

    if (inFlight) {
      if (driveListSortsEqual(inFlight.sort, sort)) {
        queuedFolderChildrenRefreshes.add(folderId)
        return
      }
      inFlight.controller.abort()
    }

    const controller = new AbortController()
    inFlightFolderChildrenLoads.set(folderId, { sort, controller })
    try {
      listenerApi.dispatch(
        UserFoldersActions.requestStarted({
          requestId,
          operationType: 'fetchChildren',
          folderId,
        })
      )

      const response = await getFolderChildren(folderId, { sort, signal: controller.signal })

      // `abort()` only rejects a request still in flight. If the last page resolved just before a
      // sort change, this continuation is already queued and would land the old order on top of the
      // new one. The replacement owns the folder state; only retire this request's bookkeeping.
      if (controller.signal.aborted) {
        listenerApi.dispatch(UserFoldersActions.requestCancelled({ requestId }))
        return
      }

      listenerApi.dispatch(
        UserFoldersActions.setFolderChildren({
          parentId: folderId,
          children: response.folders,
          sort,
        })
      )
      listenerApi.dispatch(UserFoldersActions.requestSucceeded({ requestId }))
    } catch (error) {
      // A superseded request is not a failure; its replacement owns the folder's loading state.
      if (isAbortError(error)) {
        listenerApi.dispatch(UserFoldersActions.requestCancelled({ requestId }))
        return
      }

      const errorMessage = error instanceof Error ? error.message : 'Failed to fetch folder children'
      const statusCode = getApiStatusCode(error)

      if (isMissingOrDeniedStatus(statusCode)) {
        listenerApi.dispatch(UserFilesActions.clearFolderFiles({ folderId }))
        listenerApi.dispatch(UserFoldersActions.clearFolderChildren({ folderId }))
        if (listenerApi.getState().userFoldersState.currentFolderId === folderId) {
          listenerApi.dispatch(UserFoldersActions.urlInitializationFailed({ folderId, error: errorMessage, statusCode }))
        }
      }

      listenerApi.dispatch(UserFoldersActions.requestFailed({ requestId, error: errorMessage }))
    } finally {
      // Only tidy up if this request is still the current one for the folder; an aborted request
      // must not clear the entry or replay the queue on behalf of the request that replaced it.
      if (inFlightFolderChildrenLoads.get(folderId)?.controller === controller) {
        inFlightFolderChildrenLoads.delete(folderId)

        // Names this request's own sort rather than letting the replay inherit: on the success and
        // failure paths this request was never superseded, so its sort is the most recent one asked
        // for — and on failure the cache still describes the sort this one was replacing.
        if (queuedFolderChildrenRefreshes.delete(folderId)) {
          listenerApi.dispatch(UserFoldersActions.fetchFolderChildren({ folderId, sort }))
        }
      }
    }
  },
})

// ============================================================================
// Restore Folder
// ============================================================================

startListener({
  actionCreator: UserFoldersActions.restoreFolder,
  effect: async (action, listenerApi) => {
    const { folderId } = action.payload
    const requestId = nanoid()

    try {
      listenerApi.dispatch(
        UserFoldersActions.requestStarted({
          requestId,
          operationType: 'restore',
          folderId,
        })
      )

      const restoredFolder = await restoreFolderApi(folderId)

      listenerApi.dispatch(UserFoldersActions.upsertFolder(restoredFolder))
      listenerApi.dispatch(UserFoldersActions.requestSucceeded({ requestId }))
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to restore folder'
      listenerApi.dispatch(UserFoldersActions.requestFailed({ requestId, error: errorMessage }))
    }
  },
})

// ============================================================================
// Initialize from URL
// ============================================================================

/**
 * Handles direct navigation to a folder via URL.
 * Fetches the folder, validates it belongs to active matter,
 * loads ancestor chain for breadcrumbs, and sets as current folder.
 */
startListener({
  actionCreator: UserFoldersActions.initializeFromUrl,
  effect: async (action, listenerApi) => {
    const { folderId } = action.payload
    const state = listenerApi.getState()
    const activeMatterId = state.mattersState.activeMatterId

    try {
      // 1. Fetch the target folder
      const folder = await getFolder(folderId)

      // 2. Validate folder belongs to active matter (if a specific matter is selected)
      if (activeMatterId && folder.matter_id !== activeMatterId) {
        throw new Error('Folder belongs to different matter')
      }

      // 3. Upsert the target folder
      listenerApi.dispatch(UserFoldersActions.upsertFolder(folder))

      // 4. Load ancestor chain for breadcrumbs
      let current = folder
      while (current.parent_id) {
        const existingParent = listenerApi.getState().userFoldersState.folders[current.parent_id]
        if (existingParent) break // Already have this ancestor

        const parent = await getFolder(current.parent_id)
        listenerApi.dispatch(UserFoldersActions.upsertFolder(parent))
        current = parent
      }

      // 5. Set the current folder; the mounted drive hook owns listing refreshes.
      listenerApi.dispatch(UserFoldersActions.setCurrentFolder({ folderId }))
      listenerApi.dispatch(UserFoldersActions.urlInitializationComplete())
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Failed to load folder'
      listenerApi.dispatch(UserFoldersActions.urlInitializationFailed({ folderId, error: message, statusCode: getApiStatusCode(error) }))
    }
  },
})
